import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import type { i18n as I18nInstance } from 'i18next';

import useFireFightingExercisesSchema from '/business/shared/components/questions/scope1/_hooks/useFireFightingExercisesSchema';
import FireFighting from '/business/airbp/images/icons/FireFighting.svg';
import type { QuestionConfigQuestionType } from '/index';

import useFormatSharedCustomScope1 from '/business/shared/components/pages/question/formats/customDataFormats/useFormatSharedCustomScope1';
import educationContentJson from '/business/shared/components/questions/scope1/_education/firefighting.json';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import initiativeBackground from '/business/shared/images/initiatives/fire-fighting-banner-1x.png';
import initiativeCardBackground from '/business/shared/images/initiatives/fire-fighting-card-1x.png';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import formatNumber from '/shared/functions/formatNumber';

export const getConfigureFireFightingExercises = ({
  i18n,
  hasScopeBasedOrganisation = true,
  reductionCardsSlides = ['slide1'],
}: {
  hasScopeBasedOrganisation?: boolean;
  i18n: I18nInstance;
  reductionCardsSlides?: string[];
}): QuestionConfigQuestionType => {
  return {
    breadcrumbTitle: '',
    details: { items: undefined, title: '' },
    displayLesseeField: false,
    displayNumOfVehicles: false,
    editPageTitle: '',
    freeTypeDistance: false,
    parent: undefined,
    path: [],
    reductionPageChartTitle: '',
    sidebarShort: false,
    subQuestions: [],
    type: 'question',
    id: 'fire-fighting-exercises',
    useHookLoader: useFireFightingExercisesSchema,
    table: {
      title: 'Calculated carbon emissions for activity data',
      totalType: basketTableTotalTypes.TONNAGE,
      headings: ['Emission source', 'Quantity', 'Carbon emissions'],
      cells: [
        () => 'What?!',
        'quantity',
        ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} tCO\u2082e`,
      ].filter((item) => !!item),
      isAnswerEditable: true,
      tabName: 'Activity data',
      mainQuestionTitle: 'Calculated carbon emissions for firefighting exercises: ',
      custom: {
        columns: ['emissionSource', 'intensityValue', 'unitOfMeasurement', 'quantity', 'carbonEmissions'],
      },
    },
    initiative: {
      formatForInitiativeSummary: () => {
        return {
          isShowingReportingPeriod: false,
          isShowingReportingPeriodNoReduction: false,
          category: false,
          description: false,
          descriptionNoReduction: false,
        };
      },
      getVisuals: ({ isBpOffering }) => {
        if (!isBpOffering) {
          return {
            description: false,
            useMask: false,
            background: initiativeBackground,
            cardBackground: initiativeCardBackground,
          };
        }
        if (isBpOffering) {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        }
      },
    },
    scopeCategory: hasScopeBasedOrganisation
      ? questionScopeCategories.SCOPE_1
      : questionScopeCategories.BUILDING_EMISSIONS,
    isCompletePropName: 'isFireFightingComplete',
    apiKey: 'calculation-fire-fighting-exercises',
    title: i18n.t('business:Questions.calculations.fireFighting.title'),
    primaryI18nKey: 'business:QuestionPage.forms.fireFighting.key',
    subTextI18nKey: 'business:QuestionPage.forms.fireFighting.tip',
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.fireFighting.key',
    reductionPageKey: 'fireFighting',
    reductionPageCards: reductionCardsSlides,
    icon: FireFighting,
    selector: {
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: false,
      para: 'To calculate emissions associated with fire suppression activities and training undertaken by your business, you can use a combination of activity data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
          requiredData: 'Quantity used during the reporting period.',
        },
        {
          title: questionSelectorOptions.CUSTOM,
          requiredData: 'Fire suppression type, intensity data/absolute emissions, quantity.',
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: educationContentJson,
    },
    useQuestionBranchCustomLoader: () => useFormatSharedCustomScope1({ questionFormatName: 'fire-fighting-exercises' }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter data about fire suppression activities and training undertaken by your business during the reporting
          period: <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          How much carbon dioxide (CO<sub>2</sub>) was used for fire suppression activities and training by your
          business during the reporting period: <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
